import type { TableColumnsType } from 'antd';
import type {
  AppUser,
  ConfigurationItem,
  ConfigurationItemToEdit,
  ParsedConfigurationItem,
  RenderSettingsScope,
  RenderSettingsType
} from 'common/models';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import type { ScheduleConfiguration } from 'pages/schedule/models';

import { getMinutes } from './timeHelpers';

// Supported render settings, value keys
// Schedule
// - object of ScheduleConfiguration
// Orders
// - table
// OrderRequests
// - table
// Customers
// - table
// Service objects
// - table
// Material
// - table
// Store
// - table
// Order add
// - Customer table - customerPart
// - InvoiceCustomerTable - invoiceCustomerPart
// - Service Objects table - serviceObjectsPart
// Interventions
// - table

export const DEFAULT_CUSTOMER_TABLE_COLUMNS = [
  'kind',
  'name',
  'address',
  'identificationNumber',
  'categoryName'
];

export const CUSTOMER_RENDER_SETTINGS = [
  'name',
  'identificationNumber',
  'taxNumber',
  'categoryName',
  'kind',
  'typeName',
  'ownerName',
  'address',
  'primaryContactPerson',
  'contactAddress',
  'phone',
  'email',
  'externalId',
  'sendByPost',
  'description'
];

export const DEFAULT_ORDER_TABLE_COLUMNS = [
  'code',
  'name',
  'state',
  'customStateName',
  'categoryName',
  'customerName',
  'address',
  'hasInterventions',
  'created',
  'aggregation'
];

export const ORDER_RENDER_SETTINGS = [
  'name',
  'code',
  'customerName',
  'customerTypeName',
  'customerCategoryName',
  'address',
  'categoryName',
  'created',
  'state',
  'ownerName',
  'estimatedDuration',
  'contactPerson',
  'priority',
  'completedAt',
  'aggregation',
  'internalNote',
  'problemDescriptionFromCustomer',
  'requestedDate',
  'hasInterventions',
  'interventionsCount',
  'externalId',
  'invoiceCustomerName',
  'customStateName',
  'lastIntervention',
  'sendByPost',
  'creationSource'
];

export const DEFAULT_ORDER_REQUEST_TABLE_COLUMNS = [
  'state',
  'categoryName',
  'customerName',
  'address',
  'serviceObjectName',
  'serviceObjectIdentificationNumber',
  'description',
  'requestedDate',
  'created'
];

export const ORDER_REQUEST_RENDER_SETTINGS = [
  'customerName',
  'categoryName',
  'requestedDate',
  'estimatedDuration',
  'created',
  'state',
  'customStateName',
  'ownerName',
  'phone',
  'email',
  'internalNote',
  'identificationNumber',
  'description',
  'address',
  'externalId',
  'serviceObjectName',
  'serviceObjectIdentificationNumber',
  'priority',
  'code'
];

export const DEFAULT_CUSTOMER_PORTAL_REQUEST_TABLE_COLUMNS = [
  'customerName',
  'title',
  'type',
  'state',
  'created'
];

export const CUSTOMER_PORTAL_REQUEST_RENDER_SETTINGS = [
  'customerName',
  'title',
  'type',
  'state',
  'created',
  'modified',
  'customStateName',
  'code'
];

export const DEFAULT_SERVICE_OBJECT_TABLE_COLUMNS = [
  'name',
  'categoryName',
  'identificationNumber',
  'count',
  'manufacturerName',
  'customerName',
  'address',
  'location',
  'nextRevision',
  'warrantyTill'
];

export const SERVICE_OBJECT_RENDER_SETTINGS = [
  'name',
  'identificationNumber',
  'manufacturerName',
  'categoryName',
  'nextRevision',
  'warrantyTill',
  'customerName',
  'address',
  'soldDate',
  'installationDate',
  'location',
  'description',
  'externalId',
  'owningCustomerName',
  'count',
  'isActive',
  'customStateName'
];

export const DEFAULT_MATERIAL_TABLE_COLUMNS = [
  'name',
  'categoryName',
  'identificationNumber',
  'price',
  'totalAmount'
];

export const MATERIAL_RENDER_SETTINGS = [
  'name',
  'categoryName',
  'price',
  'costPrice',
  'totalAmount',
  'identificationNumber',
  'supplierCode',
  'supplierName',
  'externalId'
];

export const DEFAULT_MATERIAL_AT_STORE_TABLE_COLUMNS = [
  'name',
  'materialCategoryName',
  'identificationNumber',
  'price',
  'amount'
];

export const MATERIAL_AT_STORE_RENDER_SETTINGS = [
  'name',
  'materialCategoryName',
  'price',
  'costPrice',
  'amount',
  'identificationNumber',
  'materialSupplierName',
  'supplierCode',
  'externalId'
];

export const DEFAULT_PRICE_LIST_TABLE_COLUMNS = [
  'name',
  'categoryName',
  'modified',
  'price',
  'priceListPrice',
  'priceChange'
];

export const PRICE_LIST_RENDER_SETTINGS = [
  'name',
  'modified',
  'identificationNumber',
  'supplierCode',
  'price',
  'priceListPrice',
  'costPrice',
  'categoryName',
  'supplierName',
  'externalId',
  'priceChange'
];

export const DEFAULT_ORDER_CUSTOMER_TABLE_COLUMNS = ['name', 'address', 'kind'];

export const DEFAULT_ORDER_SERVICE_OBJECTS_TABLE_COLUMNS = [
  'name',
  'categoryName',
  'identificationNumber',
  'count',
  'address',
  'location'
];

export const DEFAULT_INTERVENTION_TABLE_COLUMNS = [
  'code',
  'orderName',
  'state',
  'customStateName',
  'categoryName',
  'customerName',
  'address',
  'assignees',
  'day',
  'time',
  'aggregation'
];

export const INTERVENTION_RENDER_SETTINGS = [
  'state',
  'code',
  'orderName',
  'customerName',
  'address',
  'categoryName',
  'orderCategoryName',
  'assignees',
  'day',
  'time',
  'created',
  'aggregation',
  'primaryAssignee',
  'internalNote',
  'customStateName',
  'ownerName',
  'priority'
];

export const DEFAULT_CUSTOM_FORMS_TABLE_COLUMNS = [
  'codeNumber',
  'customFormTemplateName',
  'isSubmitted',
  'orderCode',
  'orderName',
  'customerName',
  'orderState',
  'orderCustomStateName',
  'interventionState',
  'interventionCustomStateName',
  'interventionPlannedDateTime',
  'interventionRealDateTime',
  'interventionPrimaryAssigneeFullName',
  'paymentTypeName',
  'totalPrice',
  'totalPriceWithVat',
  'aggregation'
];

export const CUSTOM_FORMS_RENDER_SETTINGS = [
  'codeNumber',
  'customFormTemplateName',
  'isSubmitted',
  'orderCode',
  'orderName',
  'customerName',
  'orderState',
  'orderCustomStateName',
  'interventionState',
  'interventionCustomStateName',
  'interventionPlannedDateTime',
  'interventionRealDateTime',
  'interventionPrimaryAssigneeFullName',
  'paymentTypeName',
  'totalPrice',
  'totalPriceWithVat',
  'aggregation'
];

export const DEFAULT_ADDRESS_SELECT_TABLE_COLUMNS = ['name', 'address', 'type', 'travelFee'];

export const ADDRESS_SELECT_RENDER_SETTINGS = [
  'name',
  'address',
  'type',
  'travelFee',
  'externalId'
];

export const DEFAULT_CONTACT_PERSON_TABLE_COLUMNS = ['fullName', 'position', 'phone', 'email'];

export const CONTACT_PERSON_RENDER_SETTINGS = [
  'fullName',
  'position',
  'phone',
  'email',
  'sendNotifications',
  'sendReports',
  'description',
  'externalId'
];

export const CUSTOMER_SERVICE_OBJECT_RENDER_SETTINGS_TO_HIDE = ['customerName'];

export const SERVICE_OBJECT_RENDER_SETTINGS_TO_HIDE = ['count'];

export const TECHNICIAN_RENDER_SETTINGS_TO_HIDE = [
  'aggregation',
  'totalPrice',
  'totalPriceWithVat'
];

export const getColumnsToRender = <T>(columns: TableColumnsType<T>, selectedColumns: string[]) => {
  const filteredColumns = columns.filter((x) => selectedColumns.includes(x.key as string));

  return filteredColumns.sort((a, b) => {
    return selectedColumns.indexOf(a.key as string) - selectedColumns.indexOf(b.key as string);
  });
};

export const getScopeOptions = (scope: RenderSettingsScope) => {
  switch (scope) {
    case 'customers':
      return CUSTOMER_RENDER_SETTINGS;
    case 'material':
      return MATERIAL_RENDER_SETTINGS;
    case 'orderrequests':
      return ORDER_REQUEST_RENDER_SETTINGS;
    case 'orders':
      return ORDER_RENDER_SETTINGS;
    case 'serviceobjects':
      return SERVICE_OBJECT_RENDER_SETTINGS;
    case 'storedetail':
      return MATERIAL_AT_STORE_RENDER_SETTINGS;
    case 'interventions':
      return INTERVENTION_RENDER_SETTINGS;
    case 'order_customer':
      return CUSTOMER_RENDER_SETTINGS;
    case 'order_invoicecustomer':
      return CUSTOMER_RENDER_SETTINGS;
    case 'order_serviceobjects':
      return SERVICE_OBJECT_RENDER_SETTINGS;
    case 'request_customerportal':
      return CUSTOMER_PORTAL_REQUEST_RENDER_SETTINGS;
    case 'price_list':
      return PRICE_LIST_RENDER_SETTINGS;
    case 'address_select':
      return ADDRESS_SELECT_RENDER_SETTINGS;
    case 'order_contactperson':
      return CONTACT_PERSON_RENDER_SETTINGS;
    case 'customforms':
      return CUSTOM_FORMS_RENDER_SETTINGS;
    default:
      return [];
  }
};

export const getScopeDefaultColumns = (scope: RenderSettingsScope) => {
  switch (scope) {
    case 'customers':
      return DEFAULT_CUSTOMER_TABLE_COLUMNS;
    case 'material':
      return DEFAULT_MATERIAL_TABLE_COLUMNS;
    case 'orderrequests':
      return DEFAULT_ORDER_REQUEST_TABLE_COLUMNS;
    case 'orders':
      return DEFAULT_ORDER_TABLE_COLUMNS;
    case 'serviceobjects':
      return DEFAULT_SERVICE_OBJECT_TABLE_COLUMNS;
    case 'storedetail':
      return DEFAULT_MATERIAL_AT_STORE_TABLE_COLUMNS;
    case 'interventions':
      return DEFAULT_INTERVENTION_TABLE_COLUMNS;
    case 'order_customer':
      return DEFAULT_ORDER_CUSTOMER_TABLE_COLUMNS;
    case 'order_invoicecustomer':
      return DEFAULT_ORDER_CUSTOMER_TABLE_COLUMNS;
    case 'order_serviceobjects':
      return DEFAULT_ORDER_SERVICE_OBJECTS_TABLE_COLUMNS;
    case 'request_customerportal':
      return DEFAULT_CUSTOMER_PORTAL_REQUEST_TABLE_COLUMNS;
    case 'price_list':
      return DEFAULT_PRICE_LIST_TABLE_COLUMNS;
    case 'address_select':
      return DEFAULT_ADDRESS_SELECT_TABLE_COLUMNS;
    case 'order_contactperson':
      return DEFAULT_CONTACT_PERSON_TABLE_COLUMNS;
    case 'customforms':
      return DEFAULT_CUSTOM_FORMS_TABLE_COLUMNS;
    default:
      return [];
  }
};

export const isLocalRenderSettingChanges = (originalColumns: string[], newColumns: string[]) => {
  const originalColumnsString = JSON.stringify(originalColumns);
  const newColumnsString = JSON.stringify(newColumns);

  return originalColumnsString !== newColumnsString;
};

export const isDefaultRenderSettings = (
  scope: RenderSettingsScope,
  selectedColumns: string[],
  columnsToHide?: string[]
) => {
  const defaultColumnsString = JSON.stringify(
    getScopeDefaultColumns(scope).filter((x) => !columnsToHide?.includes(x))
  );

  const selectedColumnsString = JSON.stringify(selectedColumns);

  return defaultColumnsString === selectedColumnsString;
};

export const transformConfigurationItem = (
  item: ParsedConfigurationItem,
  type: RenderSettingsType,
  values: any,
  isCompanyDefault?: boolean
): ConfigurationItemToEdit => {
  if (isDefaultRenderSettings(item.key as any, values) || values.length === 0) {
    return {
      isCompanyDefault,
      key: item.key,
      value: ''
    };
  }

  return {
    ...item,
    isCompanyDefault,
    value: JSON.stringify({
      ...item.value,
      [type]: values
    })
  };
};

export const getParsedConfigurationItem = (
  configuration: ConfigurationItem[],
  scope: RenderSettingsScope
): ParsedConfigurationItem | undefined => {
  const configurationItem = configuration.find((x) => x.key === scope);

  return configurationItem
    ? {
        ...configurationItem,
        value: configurationItem.value ? JSON.parse(configurationItem.value) : ''
      }
    : undefined;
};

export const getRenderSettingsScopeTypeValue = (
  scope: RenderSettingsScope,
  type: RenderSettingsType,
  appUser?: AppUser,
  parsedConfigurationItem?: ParsedConfigurationItem
) => {
  const isTechnician = appUser?.isTechnician && !appUser.isManager && !appUser.isAdmin;

  if (parsedConfigurationItem?.value?.[type]) {
    if (isTechnician && type === 'table') {
      return parsedConfigurationItem.value[type].filter(
        (x: string) => !TECHNICIAN_RENDER_SETTINGS_TO_HIDE.includes(x)
      );
    }

    return parsedConfigurationItem.value[type];
  }

  if (type === 'table') {
    return isTechnician
      ? getScopeDefaultColumns(scope).filter((x) => !TECHNICIAN_RENDER_SETTINGS_TO_HIDE.includes(x))
      : getScopeDefaultColumns(scope);
  }

  return parsedConfigurationItem?.value;
};

export const createConfigurationItem = (
  scope: RenderSettingsScope,
  type: RenderSettingsType,
  values: any,
  isCompanyDefault?: boolean
): ConfigurationItemToEdit => {
  if (isDefaultRenderSettings(scope, values) || values.length === 0) {
    return {
      isCompanyDefault,
      key: scope,
      value: ''
    };
  }

  return {
    isCompanyDefault,
    key: scope,
    value: JSON.stringify({
      [type]: values
    })
  };
};

export const getConfigurationItemToSave = (
  values: any,
  scope: RenderSettingsScope,
  type: RenderSettingsType,
  parsedItem?: ParsedConfigurationItem,
  isCompanyDefault?: boolean
): ConfigurationItemToEdit => {
  const transformedItem = parsedItem
    ? transformConfigurationItem(parsedItem, type, values, isCompanyDefault)
    : createConfigurationItem(scope, type, values, isCompanyDefault);

  return transformedItem;
};

export const getScopeTranslateKey = (scope: RenderSettingsScope) => {
  if (scope === 'order_serviceobjects') {
    return 'serviceobjects';
  }

  if (scope === 'order_customer' || scope === 'order_invoicecustomer') {
    return 'customers';
  }

  return scope;
};

export const getDefaultScheduleVisualSettings = (): ScheduleConfiguration => ({
  visibleDays: ['1', '2', '3', '4', '5'],
  defaultView: 'resourceTimeline',
  visibleStart: getMinutes(dayjs.utc().set('hours', 8).set('minutes', 0)),
  visibleEnd: getMinutes(dayjs.utc().set('hours', 18).set('minutes', 0)),
  calendarEventsLimit: 0,
  orderItemConfig: {
    internalNote: false,
    problemDescriptionFromCustomer: false,
    requestedDate: false,
    orderCustomState: false,
    contactPhone: false,
    priority: false,
    name: false,
    stateTag: true
  },
  interventionItemConfig: {
    visible: [
      'code',
      'customerName',
      'address',
      'duration',
      'interventionCategoryName',
      'resources'
    ],
    firstRow: ['code', 'customerName']
  }
});

export const isScheduleVisualSettingsDefault = (config: ScheduleConfiguration) => {
  const defaultConfig = getDefaultScheduleVisualSettings();

  return isEqual(config, defaultConfig);
};

export const replaceOwnerForOwnerNameInOrderRequestsConfig = (
  configuration: ParsedConfigurationItem
): ConfigurationItemToEdit | undefined => {
  const originalValue = configuration.value?.['table'];

  if (originalValue && Array.isArray(originalValue) && originalValue.includes('owner')) {
    const newValue = originalValue.map((item: string) => (item === 'owner' ? 'ownerName' : item));

    const itemToEdit = getConfigurationItemToSave(
      newValue,
      'orderrequests',
      'table',
      configuration
    );

    return itemToEdit;
  }

  return undefined;
};
